import { useState } from 'react';
import { Layers, X } from 'lucide-react';
import { AddAction } from './add-action';
import type { ShownPhoto } from '../lib/area-photos';
import { useDeviceQuery } from '../lib/hooks';
import { useAreaPaintChoices } from '../lib/area-paint-choices';
import { areaHours, measuredPaintStates, METHOD_FIELDS, surfaceCount, surfaceTotalsIn } from '@higgins/m2q-pricing';
import { loadSurfaceTypes } from '../lib/reference';
import {
  addGroup,
  addLine,
  addSurface,
  duplicateGroup,
  removeGroup,
  removeLine,
  removeSurface,
  renameSurface,
  setPaintFlag,
  setUnit,
  updateGroup,
  updateLine,
  updateSettings,
  type MeasuredSurface,
  type SurfacesDoc,
  type SurfaceType,
} from '@higgins/m2q-pricing';
import { WASH_SIDE } from '../lib/site-wash';
import { AreaWash } from './area-wash';
import { SurfaceCard } from './surface-card';
import { SurfaceGroupHeader } from './surface-group';
import { formatHours, storedHours } from './surface-format';

interface SurfaceListProps {
  doc: SurfacesDoc;
  /** The area's photos; each card shows the ones taken of its surface. */
  photos: ShownPhoto[];
  jobId: string;
  areaId: string;
  /** The area's side; the picker offers that side's surfaces plus those for both. */
  side: string | null;
  /** Every change, with the area's new measured hours. */
  onChange: (doc: SurfacesDoc, measuredHours: number) => void;
}

const BOTH = 'Both';

/** "Length × Height", "Run (m)" — what the measurer will enter for this type. */
function methodSummary(method: string | null): string {
  const fields = METHOD_FIELDS[method ?? ''];
  if (!fields) return method ?? '';
  return fields.b ? `${fields.a} × ${fields.b}` : fields.a;
}

function SurfacePicker({ types, onPick, onClose }: { types: SurfaceType[]; onPick: (t: SurfaceType) => void; onClose: () => void }) {
  return (
    <div className="rounded-lg border border-slate-300 bg-white">
      <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2">
        <span className="font-semibold">Add surface</span>
        <button type="button" onClick={onClose} aria-label="Close" className="flex h-11 w-11 items-center justify-center text-slate-500">
          <X className="h-4 w-4" />
        </button>
      </div>
      {types.length === 0 ? (
        <p className="p-3 text-sm text-slate-600">No surface types on this device yet — open a job while you have signal.</p>
      ) : (
        <div className="grid grid-cols-2 gap-2 p-3 md:grid-cols-3 lg:grid-cols-4">
          {types.map((t) => (
            <button key={t.id} type="button" onClick={() => onPick(t)} className="min-h-14 rounded-md border border-slate-200 px-3 py-2 text-left hover:border-primary">
              <span className="block text-sm text-slate-900">{t.name}</span>
              <span className="block text-xs text-slate-500">
                {methodSummary(t.method)} · {t.productionRate ?? '—'}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Where the picker adds a surface: the area itself (null) or a repeat group. */
type PickTarget = { groupId: string | null } | null;

/**
 * The area's surfaces: one card each, then each repeat group ("Balcony × 24")
 * with its own surfaces; the add-surface picker; the area's total hours.
 */
export function SurfaceList({ doc, photos, jobId, areaId, side, onChange }: SurfaceListProps) {
  const [picking, setPicking] = useState<PickTarget>(null);
  // Cards closed with Done or their header (#416); kept on screen only, so every card opens again after a reload.
  const [closed, setClosed] = useState<ReadonlySet<string>>(new Set());
  const setOpen = (id: string, open: boolean) =>
    setClosed((was) => {
      const next = new Set(was);
      if (open) next.delete(id);
      else next.add(id);
      return next;
    });
  // Adding a surface closes the cards already there, so the new one is the one open (scoates, 20 Oct, #416).
  const pickFor = (groupId: string | null) => {
    setClosed(new Set(doc.surfaces.map((s) => s.id)));
    setPicking({ groupId });
  };
  const types = useDeviceQuery(loadSurfaceTypes, []);
  const paintChoices = useAreaPaintChoices(jobId, areaId);
  const byId = types?.byId ?? new Map<string, SurfaceType>();
  const offered = (types?.list ?? []).filter((t) => !side || t.side === side || t.side === BOTH);

  const apply = (next: SurfacesDoc) => onChange(next, areaHours(next, byId));
  const external = side === WASH_SIDE;
  const groupIds = new Set(doc.groups.map((g) => g.id));
  // A surface whose group has gone is shown with the area's own (it counts once).
  const ungrouped = doc.surfaces.filter((s) => !s.groupId || !groupIds.has(s.groupId));

  const card = (s: MeasuredSurface) => (
    <SurfaceCard
      key={s.id}
      surface={s}
      type={byId.get(s.typeId)}
      photos={photos.filter((p) => p.surfaceId === s.id)}
      jobId={jobId}
      areaId={areaId}
      count={surfaceCount(doc, s)}
      unpriced={external && doc.washOnly === true}
      paintStates={measuredPaintStates(doc, s, byId.get(s.typeId), paintChoices?.[s.id])}
      onAddLine={() => apply(addLine(doc, s.id))}
      onUpdateLine={(lineId, change) => apply(updateLine(doc, s.id, lineId, change))}
      onRemoveLine={(lineId) => apply(removeLine(doc, s.id, lineId))}
      onSettings={(settings) => apply(updateSettings(doc, s.id, settings))}
      onUnit={(unit) => apply(setUnit(doc, s.id, unit))}
      onRename={(name) => apply(renameSurface(doc, s.id, name))}
      onPaintFlag={(flag) => apply(setPaintFlag(doc, s.id, flag))}
      onRemove={() => apply(removeSurface(doc, s.id))}
      open={!closed.has(s.id)}
      onOpenChange={(open) => setOpen(s.id, open)}
    />
  );
  const picker = (groupId: string | null) =>
    picking && picking.groupId === groupId && (
      <SurfacePicker
        types={offered}
        onClose={() => setPicking(null)}
        onPick={(t) => {
          apply(addSurface(doc, t, groupId ?? undefined));
          setPicking(null);
        }}
      />
    );

  return (
    <div className="space-y-2">
      {ungrouped.map(card)}
      {picker(null)}
      {doc.groups.map((g) => {
        const members = doc.surfaces.filter((s) => s.groupId === g.id);
        const hours = members.reduce((sum, s) => sum + surfaceTotalsIn(doc, s, byId).hours, 0);
        return (
          <div key={g.id}>
            <SurfaceGroupHeader
              group={g}
              hours={hours}
              onChange={(change) => apply(updateGroup(doc, g.id, change))}
              onAddSurface={() => pickFor(g.id)}
              onDuplicate={() => apply(duplicateGroup(doc, g.id))}
              onRemove={() => apply(removeGroup(doc, g.id))}
            />
            <div className="space-y-2 rounded-b-lg border border-t-0 border-slate-300 p-2">
              {members.map(card)}
              {members.length === 0 && !picking && <p className="px-1 text-xs text-slate-500">Add the surfaces of one {g.name.toLowerCase() || 'item'}; the count multiplies them.</p>}
              {picker(g.id)}
            </div>
          </div>
        );
      })}
      <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
        {!picking ? (
          <span className="flex flex-wrap gap-2">
            <AddAction onClick={() => pickFor(null)}>Add surface</AddAction>
            <AddAction icon={Layers} onClick={() => apply(addGroup(doc, `Group ${doc.groups.length + 1}`))}>
              Add repeat group
            </AddAction>
          </span>
        ) : (
          <span />
        )}
        <span className="text-sm">
          Area total <span className="font-medium tabular-nums">{formatHours(storedHours(areaHours(doc, byId)))}</span>
        </span>
      </div>
      {/* At the bottom of the area's surfaces, external areas only (#510). */}
      {external && <AreaWash doc={doc} types={byId} onChange={apply} />}
    </div>
  );
}
